import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid3X3 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { api } from '@/lib/api';
import { SEM } from '@/components/client/SuiteAnalytics';

// Founder-approved product enhancement (2026-06, readability remediation) —
// department × competency coverage from CANONICAL data (positions → role
// profiles → requirements → linked published instruments → released results).
// Matrix orientation: competencies as ROWS (full readable names, no rotation),
// departments as COLUMNS. No line charts. Never fabricate results.

export function CompetencyCoverageHeatmap() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/client/talent-competency/coverage-heatmap')
      .then((r) => setData(r.data))
      .catch(() => setData({ departments: [], competencies: [], cells: {}, summary: {} }));
  }, []);

  if (!data) return null;
  const empty = !data.departments.length || !data.competencies.length;

  const STATE_META = {
    evaluated: { color: SEM.green, label: t('tco.heatmap.evaluated', { defaultValue: 'Evaluada (resultado liberado)' }) },
    covered: { color: SEM.blue, label: t('tco.heatmap.covered', { defaultValue: 'Instrumento vinculado' }) },
    gap: { color: SEM.yellow, label: t('tco.heatmap.gap', { defaultValue: 'Sin instrumento vinculado' }) },
  };
  const notRequiredLabel = t('tco.heatmap.notRequired', { defaultValue: 'No requerida' });

  return (
    <Card className="p-5 shadow-card" data-testid="tco-heatmap-card">
      <div className="flex items-center gap-2 mb-1">
        <Grid3X3 className="h-5 w-5 text-primary" />
        <h2 className="font-semibold">
          {t('tco.heatmap.title', { defaultValue: 'Cobertura de competencias por departamento' })}
        </h2>
      </div>
      <p className="text-sm text-muted-foreground mb-4" data-testid="tco-heatmap-explainer">
        {t('tco.heatmap.explainer', { defaultValue: 'Muestra qué competencias son requeridas por los puestos formales de cada departamento y si existe un instrumento vinculado para evaluarlas. No representa resultados de desempeño.' })}
      </p>
      {empty ? (
        <div className="py-8 text-center text-sm text-muted-foreground" data-testid="tco-heatmap-empty">
          <Grid3X3 className="h-7 w-7 opacity-30 mx-auto mb-2" />
          {t('tco.heatmap.empty', { defaultValue: 'Aún no hay puestos formales con Perfiles de Puesto y competencias configuradas. Configura las competencias de tus puestos en "Configurar competencias" para generar este mapa.' })}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto" data-testid="tco-heatmap-grid">
            <table className="w-full text-sm border-separate" style={{ borderSpacing: '0 4px' }}>
              <thead>
                <tr>
                  <th className="text-left text-xs uppercase tracking-wider font-semibold text-muted-foreground pb-1 pr-3 sticky left-0 bg-card z-10 min-w-[220px]">
                    {t('tco.heatmap.competency', { defaultValue: 'Competencia' })}
                  </th>
                  {data.departments.map((d) => (
                    <th key={d.id} className="text-center text-xs uppercase tracking-wider font-semibold text-muted-foreground pb-1 px-2 whitespace-nowrap min-w-[120px]" title={d.name}>
                      {d.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.competencies.map((c) => (
                  <tr key={c.code} data-testid={`tco-heatmap-row-${c.code}`}>
                    <td className="pr-3 py-1 font-medium sticky left-0 bg-card z-10 min-w-[220px] max-w-[340px]">
                      <span className="block leading-snug">{c.name}</span>
                    </td>
                    {data.departments.map((d) => {
                      const state = (data.cells[d.id] || {})[c.code];
                      const meta = state ? STATE_META[state] : null;
                      const tip = [
                        `${t('tco.heatmap.dept', { defaultValue: 'Departamento' })}: ${d.name}`,
                        `${t('tco.heatmap.competency', { defaultValue: 'Competencia' })}: ${c.name}`,
                        `${t('tco.heatmap.status', { defaultValue: 'Estado' })}: ${meta ? meta.label : notRequiredLabel}`,
                      ].join('\n');
                      return (
                        <td key={d.id} className="px-2 py-1 text-center" data-testid={`tco-heatmap-cell-${d.id}-${c.code}`}>
                          <div
                            className="mx-auto h-7 w-full max-w-[140px] rounded-md flex items-center justify-center"
                            title={tip}
                            aria-label={tip}
                            style={{ background: meta ? `${meta.color}22` : 'hsl(var(--muted))', opacity: meta ? 1 : 0.45 }}
                          >
                            <span className="h-3.5 w-3.5 rounded-full" style={{ background: meta ? meta.color : 'hsl(var(--muted-foreground) / 0.35)' }} />
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4 text-xs text-muted-foreground" data-testid="tco-heatmap-legend">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-3 w-3 rounded-full" style={{ background: SEM.green }} />
              {STATE_META.evaluated.label}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-3 w-3 rounded-full" style={{ background: SEM.blue }} />
              {STATE_META.covered.label}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-3 w-3 rounded-full" style={{ background: SEM.yellow }} />
              {STATE_META.gap.label}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-3 w-3 rounded-full bg-muted-foreground/30" />
              {notRequiredLabel}
            </span>
          </div>
        </>
      )}
    </Card>
  );
}
